/**
 * Keeps one user's server permission snapshot current for the
 * SessionPermissionsFromBackend flag. See handbook/session-permissions.md.
 */

export type PermissionSnapshot = {
  permissions: string[];
  version: string;
};

export type PermissionSyncMode = "api" | "handshake";

export const PERMISSION_RETRY_DELAY_MS = 5_000;
export const PERMISSION_RECONCILE_INTERVAL_MS = 60_000;

// Versions are opaque, case-sensitive checksums: compare with ===, never order.
export function isPermissionSnapshot(
  value: unknown,
): value is PermissionSnapshot {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<PermissionSnapshot>;
  return (
    typeof candidate.version === "string" &&
    candidate.version.length > 0 &&
    Array.isArray(candidate.permissions) &&
    candidate.permissions.every((permission) => typeof permission === "string")
  );
}

export function getPermissionVersion(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;

  const version = (value as { version?: unknown }).version;
  return typeof version === "string" && version.length > 0
    ? version
    : undefined;
}

function isSameSnapshot(a: PermissionSnapshot, b: PermissionSnapshot) {
  return (
    a.version === b.version &&
    a.permissions.length === b.permissions.length &&
    a.permissions.every(
      (permission, index) => permission === b.permissions[index],
    )
  );
}

// A confirmed authorization failure revokes the last snapshot; anything else
// (network, 5xx, an invalid body) is transient and keeps it.
function isRevokingFailure(error: unknown) {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403 || status === 404;
}

export type PermissionSyncOptions = {
  fetchSnapshot: (signal: AbortSignal) => Promise<unknown>;
  /** Returns the hub handshake, or null while the hub is not connected. */
  getHandshake: () => ((version: string) => Promise<unknown>) | null;
  /** Called after the first attempt settles and whenever the snapshot changes. */
  onChange: (snapshot: PermissionSnapshot | null) => void;
  onError: (error: unknown) => void;
};

export type PermissionSync = {
  /**
   * Queues a refresh. Pass the version from a `permissions-changed` push so a
   * push matching the confirmed snapshot can be skipped.
   */
  request: (mode: PermissionSyncMode, pushedVersion?: string) => void;
  stop: () => void;
};

export function startPermissionSync({
  fetchSnapshot,
  getHandshake,
  onChange,
  onError,
}: PermissionSyncOptions): PermissionSync {
  const controller = new AbortController();
  let active = true;
  let running = false;
  let pending: PermissionSyncMode | null = null;
  let snapshot: PermissionSnapshot | null = null;
  let published = false;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const fetchFresh = async () => {
    const data = await fetchSnapshot(controller.signal);
    if (!isPermissionSnapshot(data)) {
      throw new Error("Invalid session permissions response");
    }
    return data;
  };

  // Resolves to a newer snapshot, or null when the confirmed one is current.
  const resolve = async (
    mode: PermissionSyncMode,
  ): Promise<PermissionSnapshot | null> => {
    const confirmed = snapshot;
    const handshake = mode === "handshake" && confirmed ? getHandshake() : null;
    if (!confirmed || !handshake) return fetchFresh();

    try {
      const reply = await handshake(confirmed.version);
      if (isPermissionSnapshot(reply)) return reply;
      return getPermissionVersion(reply) === confirmed.version
        ? null
        : fetchFresh();
    } catch {
      // HTTP tells a missing or forbidden user apart from a hub failure.
      return fetchFresh();
    }
  };

  const drain = async () => {
    running = true;

    while (active && pending) {
      const mode: PermissionSyncMode = pending;
      pending = null;
      const previous = snapshot;

      try {
        const fresh = await resolve(mode);
        if (!active) return;

        // Apply as returned, including an empty revocation.
        if (fresh && !(snapshot && isSameSnapshot(snapshot, fresh))) {
          snapshot = fresh;
        }
        clearTimeout(retry);
      } catch (error) {
        if (!active) return;

        if (isRevokingFailure(error)) snapshot = null;
        onError(error);
        clearTimeout(retry);
        retry = setTimeout(() => request("api"), PERMISSION_RETRY_DELAY_MS);
      }

      if (!published || snapshot !== previous) {
        published = true;
        onChange(snapshot);
      }
    }

    running = false;
  };

  const request = (mode: PermissionSyncMode, pushedVersion?: string) => {
    if (!active) return;

    // A push can predate an in-flight response, so only skip a matching push
    // when nothing is in flight.
    if (
      !running &&
      pushedVersion !== undefined &&
      pushedVersion === snapshot?.version
    ) {
      return;
    }

    pending = pending === "api" ? "api" : mode;
    if (!running) void drain();
  };

  request("api");

  // Recovers from missed pushes and an unavailable hub: the cheap checksum
  // handshake while connected, HTTP otherwise.
  const reconcile = setInterval(
    () => request(getHandshake() ? "handshake" : "api"),
    PERMISSION_RECONCILE_INTERVAL_MS,
  );

  return {
    request,
    stop: () => {
      active = false;
      controller.abort();
      clearTimeout(retry);
      clearInterval(reconcile);
    },
  };
}
